import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import {
  HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX,
  HOME_LIST_TABLE_INACTIVE_TEXT_SX,
  HOME_LIST_TABLE_TOKENS,
} from "../../styles/homeListTableTokens";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useGuardedAsyncCallback } from "../../hooks/useGuardedAsyncCallback";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import {
  canDownloadTaskTableAttachment,
  downloadTaskTableAttachment,
  formatTaskTableAttachmentSize,
  parseTaskTableAttachments,
  type TaskTableAttachmentDownloadConfig,
  type TaskTableAttachmentFile,
} from "../../utils/taskTableAttachment";

const FILE_NAME_SX = {
  fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
  fontSize: HOME_LIST_TABLE_TOKENS.fontSize,
  fontWeight: HOME_LIST_TABLE_TOKENS.cellFontWeight,
  lineHeight: "20px",
  color: HOME_LIST_TABLE_TOKENS.cellColor,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
} as const;

const LINK_BUTTON_SX = {
  p: 0,
  minWidth: 0,
  textTransform: "none",
  fontFamily: HOME_LIST_TABLE_TOKENS.fontFamily,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "18px",
  flexShrink: 0,
} as const;

const DOWNLOAD_ICON_SX = {
  color: "#52C41A",
  "&:hover": { backgroundColor: "rgba(82, 196, 26, 0.08)" },
} as const;

export type TaskTableAttachmentCellProps = Readonly<{
  label: string;
  value: unknown;
  extraState: Record<string, unknown>;
  downloadConfig: TaskTableAttachmentDownloadConfig;
  checkDownloadable?: boolean;
}>;

function AttachmentFileRow({
  file,
  checkDownloadable,
  onDownload,
  showSize = false,
  showIcon = false,
}: Readonly<{
  file: TaskTableAttachmentFile;
  checkDownloadable: boolean;
  onDownload: (file: TaskTableAttachmentFile) => void;
  showSize?: boolean;
  showIcon?: boolean;
}>) {
  const downloadable = canDownloadTaskTableAttachment(file, checkDownloadable);
  const sizeLabel = showSize ? formatTaskTableAttachmentSize(file.size) : "";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        minWidth: 0,
      }}
    >
      {showIcon ? (
        <InsertDriveFileOutlinedIcon
          fontSize="small"
          sx={{ color: "action.active", flexShrink: 0 }}
        />
      ) : null}
      <Typography component="span" sx={FILE_NAME_SX} title={file.name}>
        {file.name}
      </Typography>
      {sizeLabel ? (
        <Typography
          component="span"
          sx={{
            ...FILE_NAME_SX,
            color: HOME_LIST_TABLE_TOKENS.inactiveColor,
            flexShrink: 0,
            overflow: "visible",
          }}
        >
          {sizeLabel}
        </Typography>
      ) : null}
      {downloadable ? (
        <IconButton
          size="small"
          onClick={() => onDownload(file)}
          aria-label={`Download ${file.name}`}
          sx={DOWNLOAD_ICON_SX}
        >
          <CloudDownloadOutlinedIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}

/**
 * Overall Review table cell: SME Assessment attachments as downloadable links.
 * One file stays in-cell; multiple files add View More (same dialog chrome as comments).
 */
export function TaskTableAttachmentCell({
  label,
  value,
  extraState,
  downloadConfig,
  checkDownloadable = false,
}: TaskTableAttachmentCellProps) {
  const files = parseTaskTableAttachments(value);
  const [open, setOpen] = useState(false);
  const { showMessage } = useSnackbar();

  const handleDownload = useGuardedAsyncCallback(
    async (file: TaskTableAttachmentFile) => {
      try {
        await downloadTaskTableAttachment({
          file,
          downloadConfig,
          extraState,
          checkDownloadable,
        });
      } catch {
        showMessage("Download failed", "error");
      }
    },
  );

  const handleClose = () => {
    setOpen(false);
  };

  if (files.length === 0) {
    return (
      <Box sx={HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX}>
        <Typography component="span" sx={HOME_LIST_TABLE_INACTIVE_TEXT_SX}>
          —
        </Typography>
      </Box>
    );
  }

  const previewFile = files[0];

  return (
    <>
      <Box
        sx={{
          ...HOME_LIST_TABLE_CELL_CONTROL_SLOT_SX,
          alignItems: "flex-start",
          justifyContent: "center",
          flexDirection: "column",
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <AttachmentFileRow
          file={previewFile}
          checkDownloadable={checkDownloadable}
          onDownload={handleDownload}
        />
        {files.length > 1 ? (
          <Button
            variant="text"
            size="small"
            onClick={() => setOpen(true)}
            sx={LINK_BUTTON_SX}
            aria-label={`View More ${label}`}
          >
            View More
          </Button>
        ) : null}
      </Box>

      <Dialog
        open={open}
        onClose={withoutBackdropClose(handleClose)}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: "12px" } } }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontWeight: 600,
            fontSize: (theme) => theme.typography.subtitle1.fontSize,
          }}
        >
          {label}
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={DIALOG_CLOSE_BUTTON_SX}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {files.map((file, index) => (
              <AttachmentFileRow
                key={`${file.id ?? file.path ?? file.name}-${index}`}
                file={file}
                checkDownloadable={checkDownloadable}
                onDownload={handleDownload}
                showSize
                showIcon
              />
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TaskTableAttachmentCell;
